// Iter 78z++++ — Print helper for the HOVER / Blueprint / AI / ISS
// takeoff preview modals. Opens a new window, writes a self-contained
// styled HTML doc with the extracted measurements + line items, then
// fires the print dialog. The contractor walks away with a hard copy
// they can hand to a crew or staple into a job folder.
//
// Pure presentation — no API calls, no PDF backend round-trip. Works
// even if the contractor is offline once the preview modal is on
// screen.

// PROVENANCE NOTES READ IN SPANISH (Howard ruled 2026-08-03): a crew on
// site is who reads them — notes translate at print; SKU names never do.
import { currentLang, tFor } from "./i18n";
import { translateNote } from "./catalogTranslations";

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtNum = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  if (typeof n !== "number") return esc(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

// Same KEY_LABELS as HoverImportButton — copied here so the printable
// doc isn't tied to that file's internal map. Add more as needed.
const KEY_LABELS = {
  siding_sqft: "Siding (ft²)",
  outside_corner_lf: "Outside Corners (LF)",
  inside_corner_lf: "Inside Corners (LF)",
  eaves_lf: "Eaves (LF)",
  rakes_lf: "Rakes (LF)",
  soffit_sqft: "Soffit (ft²)",
  fascia_lf: "Fascia (LF)",
  gutter_lf: "Gutters (LF)",
  downspouts_count: "Downspouts (count)",
  window_count: "Windows (count)",
  door_count: "Doors (count)",
  perimeter_lf: "Perimeter (LF)",
  brick_sqft: "Brick wainscot (ft²)",
  stone_sqft: "Stone wainscot (ft²)",
  shake_sqft: "Shake (ft²)",
  board_batten_sqft: "Board & Batten (ft²)",
};

const UNIT_BY_KEY = (k) => {
  if (k.endsWith("_sqft")) return "ft²";
  if (k.endsWith("_lf")) return "LF";
  if (k.endsWith("_count")) return "";
  return "";
};

const TAB_LABELS = {
  vinyl: "Vinyl Siding",
  ascend: "Ascend Composite",
  lp_smart: "LP SmartSide",
  windows: "Windows",
  iss: "ISS Quote",
};

// BLUEPRINT READ-BACK (display-only, 2026-08-06): the verification card
// on paper — the same server-built flags the modal shows. `_lang` is
// passed in (never re-read here — the ReferenceError lesson, 2026-08-04).
function renderReadBackHtml(readback, _lang = "en") {
  if (!readback) return "";
  const T = (k, vars) => tFor(_lang, k, vars);
  const n = (v) => (v == null ? "—" : Math.round(v * 10) / 10);
  const flag = (level, text) =>
    `<div class="rb-flag rb-${level}">${esc(text)}</div>`;
  const { planes, no_planes, plane_totals, garage_banner, corners, wing_check, porch, rail } = readback;
  let planesHtml = "";
  if (garage_banner) planesHtml += flag("loud", T("bp.rb.noGarage"));
  if (no_planes) {
    planesHtml += flag("loud", T("bp.rb.noPlanes"));
  } else {
    planesHtml += `
      <table class="rb-table">
        <thead><tr>
          <th>${esc(T("bp.rb.plane"))}</th><th class="num">${esc(T("bp.rb.eave"))}</th>
          <th class="num">${esc(T("bp.rb.rake"))}</th><th class="num">${esc(T("bp.rb.gableEnds"))}</th>
          <th class="num">${esc(T("bp.rb.porchSqft"))}</th>
        </tr></thead>
        <tbody>
          ${planes.map((p) => `
            <tr>
              <td>${esc(p.label)}${p.is_porch ? " ⌂" : ""}</td>
              <td class="num">${n(p.eave_lf)}</td>
              <td class="num${p.gable_blind ? " rb-loud-cell" : ""}">${n(p.rake_lf)}</td>
              <td class="num">${p.gable_ends}</td>
              <td class="num">${p.is_porch ? n(p.porch_ceiling_sqft) : "—"}</td>
            </tr>
            ${p.gable_blind ? `<tr><td colspan="5">${flag("loud", T("bp.rb.gableBlind", { plane: p.label }))}</td></tr>` : ""}
          `).join("")}
          ${plane_totals ? `
            <tr class="rb-totals">
              <td>${esc(T("bp.rb.totals"))}</td>
              <td class="num">${n(plane_totals.eaves_lf)}</td>
              <td class="num">${n(plane_totals.rakes_lf)}</td>
              <td class="num">${plane_totals.gable_ends}</td>
              <td class="num">—</td>
            </tr>` : ""}
        </tbody>
      </table>`;
  }
  let cornersHtml = `
    <div>${esc(T("bp.rb.outside"))}: <b>${corners.outside}</b> (${n(corners.outside_lf)} LF) ·
      ${esc(T("bp.rb.inside"))}: <b>${corners.inside}</b> (${n(corners.inside_lf)} LF)
      ${corners.invariant_ok != null
        ? ` · ${esc(T("bp.rb.invariant"))}: <b class="${corners.invariant_ok ? "rb-pass" : "rb-fail"}">${esc(corners.invariant_ok ? T("bp.rb.pass") : T("bp.rb.fail"))}</b>`
        : ""}
    </div>`;
  if (corners.basis === "averaged") cornersHtml += flag("warn", T("bp.rb.basis.averaged"));
  if (corners.basis === "per_corner") cornersHtml += flag("info", T("bp.rb.basis.per_corner"));
  if (corners.basis === "missing") cornersHtml += flag("warn", T("bp.rb.basis.missing"));
  if (wing_check?.flag) {
    cornersHtml += flag("loud", T("bp.rb.wing", { fp: n(wing_check.footprint_area_sqft), rect: n(wing_check.rectangle_area_sqft) }));
  }
  const porchLevel = { plane_read: "info", plane_without_ceiling: "warn", phantom_ceiling: "loud", absent: "warn" }[porch.status] || "info";
  const porchHtml = flag(porchLevel, T(`bp.rb.porch.${porch.status}`, { sqft: n(porch.ceiling_sqft) }));
  const railHtml = (rail || []).map((f) => flag(f.level, T(`bp.rb.rail.${f.code}`, { text: f.text || "" }))).join("");
  return `
    <section class="block">
      <div class="block-title">${esc(T("bp.rb.title"))} <span class="muted small">— ${esc(T("bp.rb.readonly"))}</span></div>
      <div class="rb-sub">${esc(T("bp.rb.planes"))}</div>
      ${planesHtml}
      <div class="rb-sub">${esc(T("bp.rb.corners"))}</div>
      ${cornersHtml}
      <div class="rb-sub">${esc(T("bp.rb.porch"))}</div>
      ${porchHtml}
      ${railHtml ? `<div class="rb-sub">${esc(T("bp.rb.rail"))}</div>${railHtml}` : ""}
    </section>`;
}

function renderMeasurementsHtml(measurements) {
  const entries = Object.entries(measurements || {}).filter(
    ([k, v]) =>
      v !== null &&
      v !== undefined &&
      v !== "" &&
      typeof v !== "object" &&
      !k.startsWith("_")
  );
  if (!entries.length) return "";
  return `
    <section class="block">
      <div class="block-title">Extracted Measurements</div>
      <div class="grid">
        ${entries
          .map(
            ([k, v]) => `
          <div class="cell">
            <div class="cell-label">${esc(KEY_LABELS[k] || k)}</div>
            <div class="cell-value">${fmtNum(v)} ${esc(UNIT_BY_KEY(k))}</div>
          </div>`
          )
          .join("")}
      </div>
    </section>
  `;
}

// ONE FAMILY PER PRINT (Howard ruled 2026-08-04): which tabs each
// estimate kind is allowed to put on paper. iss keeps its service tab.
const PRINT_FAMILY_TABS = {
  siding: ["vinyl", "ascend"],
  lp_smart: ["lp_smart"],
  windows: ["windows"],
  iss: ["iss"],
};

export function scopePrintLines(lines, kind, est) {
  const fam = new Set(PRINT_FAMILY_TABS[kind] || PRINT_FAMILY_TABS.siding);
  if (kind === "lp_smart" && Array.isArray(est?.lines) && est.lines.length) {
    // The estimate IS the LP source of truth (materialized server-side).
    return est.lines.filter(
      (l) => (l.tab || "vinyl") === "lp_smart" && (Number(l.qty) || 0) > 0
    );
  }
  return (lines || []).filter((l) => fam.has(l.tab || "vinyl"));
}

// `lang` DEFAULTS TO EN (ruled 2026-08-04 — the _lang ReferenceError
// crash): print must NEVER crash regardless of language state.
function renderLinesHtml(lines, kind, _lang = "en") {
  if (!lines || !lines.length) return "";
  if (kind === "iss") {
    // ISS: single flat table.
    return `
      <section class="block">
        <div class="block-title">ISS Quote — Auto-generated Line Items (${lines.length})</div>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Item</th>
              <th class="num">Qty</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            ${lines
              .map(
                (l) => `
              <tr>
                <td class="muted">${esc(l.section || "")}</td>
                <td>
                  <div>${esc(l.name || "")}</div>
                  ${l.note ? `<div class="muted small">${esc(translateNote(l.note, _lang))}</div>` : ""}
                </td>
                <td class="num bold">${esc(l.qty)}</td>
                <td class="muted">${esc(l.unit || "")}</td>
              </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </section>
    `;
  }
  // Multi-tab (siding/windows): group by tab.
  const byTab = {};
  lines.forEach((l) => {
    const t = l.tab || "vinyl";
    (byTab[t] = byTab[t] || []).push(l);
  });
  return ["vinyl", "ascend", "lp_smart", "windows"]
    .filter((t) => byTab[t] && byTab[t].length)
    .map(
      (t) => `
      <section class="block">
        <div class="block-title">${esc(TAB_LABELS[t] || t)} — ${byTab[t].length} line${byTab[t].length === 1 ? "" : "s"}</div>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Item</th>
              <th class="num">Qty</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            ${byTab[t]
              .map(
                (l) => `
              <tr>
                <td class="muted">${esc(l.section || "")}</td>
                <td>
                  <div>${esc(l.name || "")}</div>
                  ${l.note ? `<div class="muted small">${esc(translateNote(l.note, _lang))}</div>` : ""}
                </td>
                <td class="num bold">${esc(l.qty)}</td>
                <td class="muted">${esc(l.unit || "")}</td>
              </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </section>
    `
    )
    .join("");
}

function renderOpeningsHtml(openings) {
  if (!openings || !openings.length) return "";
  return `
    <section class="block">
      <div class="block-title">Window Openings (${openings.length})</div>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th class="num">W (in)</th>
            <th class="num">H (in)</th>
            <th class="num">UI</th>
            <th>Style</th>
          </tr>
        </thead>
        <tbody>
          ${openings
            .map(
              (o) => `
            <tr>
              <td class="muted">${esc(o.hover_id || o.id || "")}</td>
              <td class="num">${esc(o.width)}</td>
              <td class="num">${esc(o.height)}</td>
              <td class="num muted">${esc(Math.round((o.width || 0) + (o.height || 0)))}</td>
              <td>${esc(o.style || o.product || "—")}</td>
            </tr>`
            )
            .join("")}
        </tbody>
      </table>
    </section>
  `;
}

/**
 * Open a new window with a printable takeoff sheet.
 *
 * @param {object} opts
 * @param {"HOVER"|"Blueprint"|"AI Photo Measure"} opts.source — banner label
 * @param {object} opts.measurements — measurements map
 * @param {Array<object>} [opts.lines] — auto-generated line items
 * @param {Array<object>} [opts.openings] — window openings (HOVER schedule)
 * @param {object} [opts.est] — estimate (customer / address / number)
 * @param {string} [opts.kind] — "siding" | "windows" | "lp_smart" | "iss"
 */
export function printTakeoff({
  source = "Takeoff",
  measurements = {},
  lines = [],
  openings = [],
  est = {},
  kind = "siding",
  readback = null,
}) {
  const customer = est?.customer_name || "Untitled";
  const address = est?.address || "—";
  const estNum = est?.estimate_number || "Draft";
  const now = new Date().toLocaleString();
  const _lang = currentLang();

  // DOOR SEPARATION AT THE EMITTER (Howard ruled 2026-08-04, third
  // lesson): the print is a FAMILY-SCOPED surface. The apply and the
  // materialize were scoped but this print built from the raw all-family
  // mapper output — an LP job printed a VINYL SIDING section. Scope HERE,
  // at the one emitter, so no caller can ever print cross-family:
  //   · lp_smart prints the ESTIMATE'S OWN LP lines (the server-rebuilt
  //     list the estimate carries — the mapper's raw LP rows are the
  //     default-profile variant, not the materialized one);
  //   · every other kind prints only its family tabs from the passed set.
  // Byte-identical on the in-family lines — this only removes rows that
  // never belonged on the printout.
  lines = scopePrintLines(lines, kind, est);

  const html = `<!doctype html>
<html lang="${_lang === "es" ? "es" : "en"}">
<head>
  <meta charset="utf-8" />
  <title>${esc(source)} Takeoff Preview — ${esc(customer)} (${esc(estNum)})</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      color: #09090B;
      margin: 0;
      padding: 20px 28px;
      font-size: 12px;
      line-height: 1.4;
    }
    .header {
      border-bottom: 2px solid #09090B;
      padding-bottom: 14px;
      margin-bottom: 18px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 16px;
    }
    .header h1 {
      font-size: 20px;
      margin: 0 0 4px 0;
      font-weight: 800;
      letter-spacing: 0.01em;
    }
    .header .source-tag {
      display: inline-block;
      background: #F97316;
      color: #09090B;
      padding: 2px 8px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      margin-bottom: 6px;
    }
    .header .meta {
      font-size: 11px;
      color: #52525B;
    }
    .header .meta strong { color: #09090B; }
    .header .right {
      text-align: right;
      font-size: 10px;
      color: #71717A;
      text-transform: uppercase;
      letter-spacing: 0.16em;
    }
    .block {
      margin-bottom: 22px;
      page-break-inside: avoid;
    }
    /* read-back verification card (grayscale-legible: borders differ too) */
    .rb-sub { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #71717A; margin: 8px 0 3px; }
    .rb-flag { border: 1px solid #E4E4E7; padding: 3px 6px; font-size: 10px; margin: 2px 0; }
    .rb-loud { border: 2px solid #B91C1C; color: #B91C1C; font-weight: 700; background: #FEF2F2; }
    .rb-warn { border: 1px dashed #92400E; color: #92400E; background: #FFFBEB; }
    .rb-info { color: #52525B; }
    .rb-table { border-collapse: collapse; width: 100%; font-size: 10.5px; margin: 2px 0; }
    .rb-table th, .rb-table td { border-bottom: 1px solid #E4E4E7; padding: 2px 6px 2px 0; text-align: left; }
    .rb-table .num { text-align: right; }
    .rb-loud-cell { color: #B91C1C; font-weight: 700; }
    .rb-totals td { font-weight: 700; border-top: 1.5px solid #09090B; }
    .rb-pass { color: #15803D; }
    .rb-fail { color: #B91C1C; text-decoration: underline; }

    .block-title {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.18em;
      color: #C2410C;
      border-bottom: 1px solid #09090B;
      padding-bottom: 3px;
      margin-bottom: 8px;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
    }
    .cell {
      border: 1px solid #E4E4E7;
      padding: 6px 8px;
    }
    .cell-label {
      font-size: 9px;
      color: #71717A;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    .cell-value {
      font-weight: 700;
      font-size: 13px;
      font-variant-numeric: tabular-nums;
      color: #09090B;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    th {
      text-align: left;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      color: #71717A;
      padding: 4px 6px;
      border-bottom: 1px solid #09090B;
      font-weight: 700;
    }
    td {
      padding: 5px 6px;
      border-bottom: 1px solid #F4F4F5;
      vertical-align: top;
    }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    td.muted { color: #52525B; }
    td.bold { font-weight: 700; color: #09090B; }
    td .small { font-size: 10px; }
    .footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #E4E4E7;
      font-size: 9px;
      color: #71717A;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      display: flex;
      justify-content: space-between;
    }
    @media print {
      body { padding: 14px; }
      .header { margin-bottom: 12px; }
      .block { margin-bottom: 14px; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="source-tag">${esc(source)} Takeoff Preview</div>
      <h1>${esc(customer)}</h1>
      <div class="meta">
        <strong>Address:</strong> ${esc(address)} &nbsp;·&nbsp;
        <strong>Estimate #:</strong> ${esc(estNum)} &nbsp;·&nbsp;
        <strong>Kind:</strong> ${esc(TAB_LABELS[kind] || kind)}
      </div>
    </div>
    <div class="right">
      <div>Printed</div>
      <div style="color:#09090B;font-weight:700;font-size:11px;">${esc(now)}</div>
    </div>
  </div>

  ${renderMeasurementsHtml(measurements)}
  ${renderReadBackHtml(readback, _lang)}
  ${renderLinesHtml(lines, kind, _lang)}
  ${renderOpeningsHtml(openings)}

  <div class="footer">
    <div>Source: ${esc(source)}</div>
    <div>Verify all quantities before ordering</div>
  </div>

  <script>
    // Fire the print dialog as soon as the doc renders. Close the
    // window automatically once the user dismisses the dialog.
    window.addEventListener("load", () => {
      setTimeout(() => {
        window.focus();
        window.print();
      }, 80);
    });
    window.addEventListener("afterprint", () => {
      setTimeout(() => window.close(), 200);
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    // Pop-up blocker stopped us. Fall back to a printable iframe in
    // the current document.
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const idoc = iframe.contentWindow.document;
    idoc.open();
    idoc.write(html);
    idoc.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 200);
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

